import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import ImageIcon from '@mui/icons-material/Image'
import DescriptionIcon from '@mui/icons-material/Description'
import MapIcon from '@mui/icons-material/Map'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import EditIcon from '@mui/icons-material/Edit'
import BigModal from '../../../../components/Modals/BigModal'
import useDocumentPreview, {
  IMAGE_EXTENSIONS,
  extensionOf,
} from '../../../../components/Documents/useDocumentPreview'
import AddNewDocument from './AddNewDocument'
import EditDocumentDialog from './EditDocumentDialog'
import { EventMetaContext } from '../SingleEvent'

const fileIcon = (extension) => {
  if (extension === 'pdf') return { Icon: PictureAsPdfIcon, tone: 'anRed' }
  if (IMAGE_EXTENSIONS.includes(extension)) {
    return { Icon: ImageIcon, tone: 'anMainBlue' }
  }
  if (extension === 'gpx') return { Icon: MapIcon, tone: 'anGreen' }
  return { Icon: DescriptionIcon, tone: 'anBlue' }
}

// Stored keys are random UUIDs, so the preview needs the display name with
// the real extension re-attached to pick the right viewer.
const previewName = (document) => {
  const extension = extensionOf(document.s3key)
  const name = document.documentName || 'Document'
  return extension && extensionOf(name) !== extension
    ? `${name}.${extension}`
    : name
}

const authorName = (author) =>
  `${author?.firstName ?? ''} ${author?.lastName ?? ''}`.trim()

const DocumentsPanel = () => {
  const { eventMeta } = useContext(EventMetaContext)
  const [documents, setDocuments] = useState(eventMeta.documents ?? [])
  const [openUpload, setOpenUpload] = useState(false)
  const [editingDocument, setEditingDocument] = useState(null)
  const { openPreview, closePreview, previewModal } = useDocumentPreview()

  // Keep the populated author - the edit endpoint returns it unpopulated
  const handleDocumentSaved = (updated) =>
    setDocuments((prev) =>
      prev.map((d) =>
        d._id === updated._id
          ? {
              ...d,
              documentName: updated.documentName,
              visibleToAllStaff: updated.visibleToAllStaff,
            }
          : d,
      ),
    )

  const handleDocumentDeleted = (docID) =>
    setDocuments((prev) => prev.filter((d) => d._id !== docID))

  const openEditor = (document) => {
    closePreview()
    setEditingDocument(document)
  }

  useEffect(() => {
    setDocuments(eventMeta.documents ?? [])
  }, [eventMeta.documents])

  // Documents created elsewhere on the page (the Post Event Report saves
  // each generated PDF here) without reloading the event
  useEffect(() => {
    const onAdded = (e) => {
      if (e.detail?.eventID !== eventMeta._id || !e.detail?.document) return
      setDocuments((prev) => [...(prev ?? []), e.detail.document])
    }
    window.addEventListener('ambunet:event-document-added', onAdded)
    return () => window.removeEventListener('ambunet:event-document-added', onAdded)
  }, [eventMeta._id])

  const sortedDocuments = useMemo(
    () =>
      [...documents]
        .filter((document) => document?.s3key)
        .sort((a, b) => moment(b.createdAt) - moment(a.createdAt)),
    [documents],
  )

  return (
    <Box
      component={Paper}
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: '1px solid rgba(15, 23, 42, 0.08)',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
        bgcolor: 'background.paper',
      }}
    >
      <Stack gap={1.5}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          <Stack gap={0.25}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Documents
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Files attached to this event
            </Typography>
          </Stack>
          <Tooltip title="Upload a document">
            <IconButton
              size="small"
              color="primary"
              onClick={() => setOpenUpload(true)}
            >
              <UploadFileIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        {sortedDocuments.length === 0 ? (
          <Stack
            alignItems="center"
            gap={1}
            sx={{
              py: 2.5,
              px: 2,
              borderRadius: 2,
              border: '1px dashed rgba(15, 23, 42, 0.18)',
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" fontWeight={700}>
              No documents yet
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Site plans, client paperwork and briefing packs uploaded here
              can be shared with all staff or kept to planners.
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<UploadFileIcon />}
              onClick={() => setOpenUpload(true)}
              sx={{ mt: 0.5 }}
            >
              Upload document
            </Button>
          </Stack>
        ) : (
          <Stack>
            {sortedDocuments.map((document) => {
              const { Icon, tone } = fileIcon(extensionOf(document.s3key))
              const visibleToAllStaff = !!document.visibleToAllStaff
              return (
                <Stack
                  key={document._id}
                  direction="row"
                  alignItems="center"
                  gap={1.25}
                  onClick={() =>
                    openPreview({
                      key: document.s3key,
                      name: previewName(document),
                      actions: (
                        <Tooltip title="Edit or delete this document">
                          <IconButton onClick={() => openEditor(document)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      ),
                    })
                  }
                  sx={{
                    px: 1,
                    py: 0.9,
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease',
                    '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.04)' },
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1.5,
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                      bgcolor: `${tone}.main`,
                      color: `${tone}.contrastText`,
                      opacity: 0.9,
                    }}
                  >
                    <Icon fontSize="small" />
                  </Box>
                  <Box minWidth={0} flex={1}>
                    <Typography variant="body2" fontWeight={700} noWrap>
                      {document.documentName || 'Untitled document'}
                    </Typography>
                    <Typography
                      component="div"
                      variant="caption"
                      color="text.secondary"
                      noWrap
                    >
                      {[
                        authorName(document.author),
                        document.createdAt &&
                          moment(document.createdAt).format('Do MMM YYYY'),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  </Box>
                  <Tooltip
                    title={
                      visibleToAllStaff
                        ? 'Visible to all staff on the read-only event view'
                        : 'Hidden from staff without the Event Planner permission'
                    }
                  >
                    <Chip
                      size="small"
                      icon={
                        visibleToAllStaff ? (
                          <VisibilityIcon />
                        ) : (
                          <VisibilityOffIcon />
                        )
                      }
                      label={visibleToAllStaff ? 'All staff' : 'Planners'}
                      color={visibleToAllStaff ? 'success' : 'default'}
                      variant="outlined"
                      sx={{
                        borderRadius: 1,
                        height: 22,
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Edit or delete">
                    <IconButton
                      size="small"
                      sx={{ flexShrink: 0 }}
                      onClick={(e) => {
                        e.stopPropagation()
                        setEditingDocument(document)
                      }}
                    >
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )
            })}
          </Stack>
        )}
      </Stack>

      {previewModal}
      <EditDocumentDialog
        eventID={eventMeta._id}
        document={editingDocument}
        onClose={() => setEditingDocument(null)}
        onSaved={handleDocumentSaved}
        onDeleted={handleDocumentDeleted}
      />
      <BigModal autoWidth open={openUpload} onClose={() => setOpenUpload(false)}>
        <AddNewDocument
          eventID={eventMeta._id}
          setDocuments={setDocuments}
          onClose={() => setOpenUpload(false)}
        />
      </BigModal>
    </Box>
  )
}

export default React.memo(DocumentsPanel)
